import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';
import { Location } from '@angular/common';
import { TaskPanelService } from '../../../services/tasks/task-panel.service';
import { Task } from '../../../interfaces/task/task';
import { ApiService } from '../../../services/base/api.service';
import { ApiEndpointsService } from '../../../services/base/api-endpoints.service';
import { ServerResponse } from '../../../interfaces/base/server.response';
import { Observable, Subject, Subscriber, TimeoutError } from 'rxjs';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { FormService } from '../../../services/base/form.service';
import { ActivatedRoute } from '@angular/router';
import { TaskService } from '../../../services/tasks/task.service';
import { Source } from '../../../interfaces/routering/source';
import { FiltersService } from '../../../services/base/filters.service';
import { BasicRegistration } from '../../../interfaces/routering/basic-registration';
import { TaskRegistration } from '../../../interfaces/routering/task-registration';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AuthenticationService } from '../../../services/base/authentication.service';
import { Gallery, GalleryItem, GalleryRef, ImageItem, VideoItem } from 'ng-gallery';
import { FileInputComponent } from 'ngx-material-file-input';
import { RemovableAttachmentComponent } from '../../../../@fury/shared/removable-attachment/removable-attachment.component';
import { MatTab, MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { debounceTime, distinctUntilChanged, take, takeUntil, timeout } from 'rxjs/operators';
import * as moment from 'moment';
import { ReassignDialogComponent } from './reassign-dialog/reassign-dialog.component';
import { QuillService } from '../../../services/base/quill.service';
import { MatFormField } from '@angular/material/form-field';
import { User } from '../../../interfaces/routering/user';
import { TaskPrintService } from '../../../services/tasks/task-print.service';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { NationaalGeoregisterSuggest } from '../../../interfaces/external-services/nationaal-georegister-suggest';
import { NationaalGeoregisterLookup } from '../../../interfaces/external-services/nationaal-georegister-lookup';
import { SearchAddressResults } from '../../../interfaces/base/search-address-results';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { IndexButton } from '../../../interfaces/base/IndexButton';
import { StandardReaction } from '../../../interfaces/configuration/standard-reaction';
import { ForwardDialogComponent } from './forward-dialog/forward-dialog.component';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { DateTimeService } from '../../../services/base/date-time.service';
import { TaskNote } from '../../../interfaces/task/task-note';
import { Filters } from '../../../interfaces/base/filters';
import { TaskWebhookService } from '../../../services/tasks/task-webhook.service';
import { Priority } from '../../../interfaces/configuration/priority';
import { LeafletMapService } from '../../../services/base/leaflet-map.service';
import { Organisation } from '../../../interfaces/routering/organisation';
import { TaskResolutionNoteAttachment } from '../../../interfaces/task/task-resolution-note-attachment';
import { Status } from '../../../interfaces/configuration/status';
import { Tile } from '../../../interfaces/routering/tile';
import { Action } from '../../../interfaces/routering/action';
import { Current } from '../../../interfaces/configuration/current';
import { LocationSelectorDialogComponent } from '../../../../@fury/shared/dialog/location-selector/location-selector-dialog.component';
import { ImpactRegistration } from '../../../interfaces/configuration/impact-registration';
import { SendReminderDialogComponent } from './send-reminder-dialog/send-reminder-dialog.component';
import { Department } from '../../../interfaces/routering/department';
import { NationaalGeoregisterRevGeo } from '../../../interfaces/external-services/nationaal-georegister-rev-geo';

@Component({
  selector: 'fury-task-editor',
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('detailExpand',
      [
        state('collapsed', style({height: '0px'})),
        state('expanded', style({height: '*'})),
        transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      ]),
  ],
})
export class EditorComponent implements OnInit, AfterViewInit, OnDestroy {

  public filters: Filters = {
    basicRegistrations: {
      preload: true,
      isLoading: false,
      data: <BasicRegistration[]>[]
    },
    taskRegistrations: {
      preload: false,
      isLoading: false,
      subscription: null,
      data: <TaskRegistration[]>[]
    },
    sources: {
      preload: true,
      isLoading: false,
      subscription: null,
      data: <Source[]>[]
    },
    priorities: {
      preload: true,
      isLoading: false,
      data: <Priority[]>[]
    },
    statuses: {
      preload: true,
      isLoading: false,
      data: <Status[]>[]
    },
    users: {
      preload: true,
      isLoading: false,
      data: <User[]>[]
    },
    tiles: {
      preload: true,
      isLoading: false,
      data: <Tile[]>[]
    },
    actions: {
      preload: true,
      isLoading: false,
      data: <Action[]>[]
    },
    currents: {
      preload: true,
      isLoading: false,
      data: <Current[]>[]
    },
    impactRegistrations: {
      preload: true,
      isLoading: false,
      data: <ImpactRegistration[]>[]
    },
    standardReactions: {
      preload: false,
      isLoading: false,
      subscription: null,
      data: <StandardReaction[]>[]
    },
  };

  public editCurrentTask: boolean = false;

  public searchTaskLocationsTimedOut: boolean = false;
  public searchTaskLocationsNoResults: boolean = false;
  public searchTaskLocationsIsLoading: boolean = false;
  public searchTaskLocations: SearchAddressResults[] = [];

  public mobileView: boolean = false;
  public topButtons: IndexButton[] = [];
  public bottomLeftButtons: IndexButton[] = [
    {
      id: 'submit-feedback',
      text: '',
      functionOrRoute: this.taskBug.bind(this),
      color: 'primary',
      icon: 'feedback',
      zIndex: 1000
    },
  ];
  public bottomRightButtons: IndexButton[] = [];

  public disableForm: boolean = false;

  public taskId: number = null;
  public task: Task = null;
  public isLoading: boolean = null;
  public isGeocodingNewRDCoordinate: boolean = false;

  public handlingGalleryMedia: GalleryItem[];
  public galleryHandlingRef: GalleryRef = null;

  @ViewChild('uploadHandlingAttachmentsInput', {static: false}) uploadHandlingAttachmentsInput: FileInputComponent;
  @ViewChild('handlingAttachmentsPlaceholder', {read: ViewContainerRef}) handlingAttachmentsPlaceholder: ViewContainerRef;
  public handlingAttachmentsRef: Array<ComponentRef<RemovableAttachmentComponent>> = [];

  public form = this.fb.group({
    number: <string>'',
    created_at: <string>'',
    priority_id: new FormControl(<number>null, Validators.required),
    status_id: new FormControl(<number>null, Validators.required),
    assigned_user_id: <number>null,
    lat: <number>null,
    lng: <number>null,
    show_new_rd_coordinate_inputs: <boolean>false,
    new_rd_lat: <number>null,
    new_rd_lng: <number>null,
    address: new FormControl(<string>null, Validators.required),
    streetname: <string>null,
    house_number: <string>null,
    house_number_addition: <string>null,
    postalcode: <string>null,
    city: <string>null,
    basic_registration_id: new FormControl(<number>null, Validators.required),
    task_registration_id: <number>null,
    source_id: <number>null,
    action_ids: new FormControl(null, Validators.required),
    current_id: <number>null,
    impact_registration_id: <number>null,
    outside_number: <string>null,
  });

  public handlingForm = this.fb.group({
    handle: <'resolved' | 'rejected'>null,
    standard_reaction_id: <number>null,
    note: <string>'',
    attachments: this.fb.array([]),
    attachmentsContainer: [''],
  });

  public noteForm = this.fb.group({
    note: <string>'',
    update_id: <number>null,
  });
  public showNoteWidget: boolean = false;

  public reopenForm = this.fb.group({
    note: <string>null,
  });
  public showReopenWidget: boolean = false;

  @ViewChild('tabGroup') private tabGroup: MatTabGroup;

  public taskDeleted: boolean = false;

  public tabWarning: string[] = [];

  public canShowHandleButtons: boolean = false;

  private previousPageFilter: string = null;

  public historyViewActivated: Subject<null> = new Subject<null>();
  private destroy$: Subject<null> = new Subject<null>();

  constructor(private cd: ChangeDetectorRef,
              private activatedRoute: ActivatedRoute,
              private apiEndpoints: ApiEndpointsService,
              private fb: FormBuilder,
              private http: HttpClient,
              private location: Location,
              private taskPrintService: TaskPrintService,
              private breakpointService: BreakpointObserver,
              public api: ApiService,
              public authenticationService: AuthenticationService,
              public formService: FormService,
              public taskService: TaskService,
              public filtersService: FiltersService,
              public taskPanelService: TaskPanelService,
              public dialog: MatDialog,
              public gallery: Gallery,
              public quillService: QuillService,
              public dateTimeService: DateTimeService,
              public taskWebhookService: TaskWebhookService,
              public leafletMapService: LeafletMapService,
  ) {
  }

  ngOnInit(): void {
    this.formService.form = this.form;
    this.isLoading = true;
    this.galleryHandlingRef = this.gallery.ref('handling');
    this.dateTimeService.cd = this.cd;

    this.previousPageFilter = this.api.filterPageName;
    this.filtersService.filters = this.filters;

    this.breakpointService.observe(Breakpoints.WebLandscape)
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: BreakpointState) => this.mobileView = !value.matches);

    Object.keys(this.filters).map((key: string): void => {
      if (this.filters[key].preload) {
        this.filters[key].isLoading = true;
        this.filters[key].subscription = this.filtersService[key]().pipe(takeUntil(this.destroy$)).subscribe((data: any): void => {
          this.filters[key].data = data;

          if (key === 'priorities') {
            if (data !== null) {
              data.forEach((d: Priority): void => {
                if (d.default === true) {
                  this.form.controls.priority_id.setValue(d.id);
                }
              });
            }
          } else if (key === 'statuses') {
            if (data !== null) {
            }
            data.forEach((d: Status): void => {
              if (d.name === 'to-do') {
                this.form.controls.status_id.setValue(d.id);
              }
            });
          }

          this.filters[key].isLoading = false;
          this.cd.detectChanges();
        });
      }
      this.cd.detectChanges();
    });

    this.form.controls.basic_registration_id.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((basicRegistrationId: number): void => {
      this.filters.taskRegistrations.isLoading = true;
      this.filters.taskRegistrations.data = [];
      this.form.controls.task_registration_id.setValue(null);
      this.cd.detectChanges();

      if (basicRegistrationId) {
        if (this.filters.taskRegistrations.subscription) {
          this.filters.taskRegistrations.subscription.unsubscribe();
        }

        this.filters.taskRegistrations.subscription = this.filtersService.taskRegistrations(basicRegistrationId).pipe(takeUntil(this.destroy$)).subscribe((data: TaskRegistration[]): void => {
            this.filters.taskRegistrations.data = data;
            this.filters.taskRegistrations.isLoading = false;

            const sourceIds: TaskRegistration[] = data.filter((d: TaskRegistration) => d.basic_registration_id === basicRegistrationId && d.source_id);
            this.filters.sources.isLoading = true;
            this.filters.sources.subscription = this.filtersService.sources().pipe(takeUntil(this.destroy$)).subscribe((d: Source[]): void => {
                this.filters.sources.data = d.filter((d2: Source): boolean => (sourceIds.length ? d2.basic_registration_ids.includes(basicRegistrationId) : true));
                this.filters.sources.isLoading = false;

                this.cd.detectChanges();
              },
              (): void => {
                this.filters.sources.isLoading = false;
                this.cd.detectChanges();
              }
            );

            this.cd.detectChanges();
          },
          (): void => {
            this.filters.taskRegistrations.isLoading = false;
            this.cd.detectChanges();
          }
        );
      } else {
        this.filters.taskRegistrations.isLoading = false;

        this.filters.sources.isLoading = true;
        this.filters.sources.subscription = this.filtersService.sources().pipe(takeUntil(this.destroy$)).subscribe((data: Source[]): void => {
            this.filters.sources.data = data;
            this.filters.sources.isLoading = false;

            this.cd.detectChanges();
          },
          (): void => {
            this.filters.sources.isLoading = false;
            this.cd.detectChanges();
          }
        );
      }
      this.cd.detectChanges();
    });

    this.form.controls.task_registration_id.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((taskRegistrationId: number): void => {
      if (taskRegistrationId) {
        const taskRegistration: TaskRegistration = this.filters.taskRegistrations.data.find((d: TaskRegistration): boolean => d.id === taskRegistrationId),
          actionEl = this.form.controls.action_ids,
          sourceEl: FormControl<number> = this.form.controls.source_id,
          priorityEl: FormControl<number> = this.form.controls.priority_id,
          priorities: Priority[] = this.filters.priorities.data,
          currentEl: FormControl<number> = this.form.controls.current_id;

        if (taskRegistration) {
          if (taskRegistration.source && sourceEl.untouched) {
            sourceEl.setValue(taskRegistration.source_id);
          } else if (sourceEl.untouched) {
            sourceEl.setValue(null);
          }

          if (taskRegistration.action_ids && actionEl.untouched) {
            actionEl.setValue(taskRegistration.action_ids);
          } else if (actionEl.untouched) {
            actionEl.setValue(null);
          }

          if (taskRegistration.priority_id && priorityEl.untouched) {
            priorityEl.setValue(taskRegistration.priority_id);
          } else if (priorityEl.untouched && priorities) {
            priorityEl.setValue(priorities.find(d => d.default)?.id);
          }

          if (taskRegistration.current && currentEl.untouched) {
            currentEl.setValue(taskRegistration.current_id);
          } else if (currentEl.untouched) {
            currentEl.setValue(null);
          }
        } else {
          if (sourceEl.untouched) {
            sourceEl.setValue(null);
          }

          if (actionEl.untouched) {
            actionEl.setValue(null);
          }

          if (priorityEl.untouched && priorities) {
            priorityEl.setValue(priorities.find(d => d.default)?.id);
          }

          if (currentEl.untouched) {
            currentEl.setValue(null);
          }
        }
      }
    });

    this.form.controls.show_new_rd_coordinate_inputs.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean): void => {
      const new_rd_lat = this.form.controls.new_rd_lat,
        new_rd_lng = this.form.controls.new_rd_lng,
        address = this.form.controls.address;
      if (value) {
        new_rd_lat.addValidators(Validators.required);
        new_rd_lat.updateValueAndValidity();
        new_rd_lng.addValidators(Validators.required);
        new_rd_lng.updateValueAndValidity();
        address.removeValidators(Validators.required);
        address.updateValueAndValidity();
      } else {
        new_rd_lat.removeValidators(Validators.required);
        new_rd_lat.updateValueAndValidity();
        new_rd_lng.removeValidators(Validators.required);
        new_rd_lng.updateValueAndValidity();
        address.addValidators(Validators.required);
        address.updateValueAndValidity();
      }
    });

    this.form.controls.address.valueChanges.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((value: string | any): void => {
      if (value) {
        this.searchTaskLocationsIsLoading = true;
        this.searchTaskLocationsNoResults = false;
        this.searchTaskLocationsTimedOut = false;

        if (typeof value === 'string') {
          this.getSearchedAddress(value).subscribe((response: NationaalGeoregisterSuggest): void => {
            if (response) {
              if (response.response.numFound > 0) {
                this.searchTaskLocations = response.response.docs;
              } else {
                this.searchTaskLocationsNoResults = true;
              }
            }

            this.searchTaskLocationsIsLoading = false;
            this.cd.detectChanges();
          });
        } else {
          this.selectTaskAddress(value.id);
        }
      }
      this.cd.detectChanges();
    });

    this.handlingForm.controls.handle.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string): void => {
      if (value) {
        this.handlingForm.controls.handle.addValidators(Validators.required);
        this.handlingForm.controls.handle.updateValueAndValidity({emitEvent: false});
        this.cd.detectChanges();

        this.filters.standardReactions.isLoading = true;
        this.filters.standardReactions.data = [];
        this.cd.detectChanges();

        if (this.filters.standardReactions.subscription) {
          this.filters.standardReactions.subscription.unsubscribe();
        }

        this.filters.standardReactions.subscription = this.filtersService.standardReactions().pipe(takeUntil(this.destroy$)).subscribe((data: StandardReaction[]): void => {
          if (data !== null) {
            this.filters.standardReactions.data = [];
            data.forEach((d: StandardReaction): void => {
              if (d.for === this.handlingForm.controls.handle.value) {
                this.filters.standardReactions.data.push(d);
              }
            });
          }

          this.filters.standardReactions.isLoading = false;
          this.cd.detectChanges();
        });
      }
    });

    this.handlingForm.controls.standard_reaction_id.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: number): void => {
      if (value) {
        const standardReaction: StandardReaction = this.filters.standardReactions.data.find((d: StandardReaction): boolean => d.id === value);

        if (standardReaction) {
          this.handlingForm.controls.note.setValue(standardReaction.content, {emitEvent: false});
        }
      } else {
        this.handlingForm.controls.note.setValue('');
      }

      this.cd.detectChanges();
    });

    this.api.generalHttpError.pipe(takeUntil(this.destroy$)).subscribe((value): void => {
      if (value) {
        this.isLoading = false;
        this.cd.detectChanges();
      }
    });

    this.taskService.taskDeletedId.pipe(takeUntil(this.destroy$)).subscribe((id: number): void => {
      if (id) {
        this.taskDeleted = true;
        this.enableOrDisableTaskEditing('disable');
      }
    });

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((): void => this.updateFormValidity());
    this.handlingForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((): void => this.updateFormValidity());
  }

  ngAfterViewInit(): void {
    this.checkFormForRequiredFields();

    this.taskPanelService.nextTaskId.pipe(takeUntil(this.destroy$)).subscribe((taskId: number | boolean): void => {
      if (taskId) {
        if (this.location.path(false).indexOf('nieuw') !== -1) {
          this.location.replaceState(this.location.path(false).replace(/nieuw+/g, 'bekijk/') + taskId);
        } else if (this.location.path(false).indexOf('rapportage') === -1) {
          this.location.replaceState(this.location.path(false).replace(/\d+/g, '') + taskId);
        }
      }

      if (taskId === false) {
        this.taskPanelService.close();
      } else {
        this.taskId = <number>taskId;
        this.resetView();
        this.taskService.taskOpenedId.next(this.taskId);

        if (!taskId) {
          this.isLoading = false;
          this.updateButtons();
        } else {
          this.isLoading = true;
          this.api.get(this.apiEndpoints.get('tasks.read', {':id': taskId}), null, true).pipe(takeUntil(this.destroy$)).subscribe((response: ServerResponse): void => {
            if (typeof response.data !== 'undefined') {
              this.task = <Task>response.data;
              this.taskPrintService.task = this.task;
              this.updateButtons();

              let address = '';
              if (this.task.streetname !== null) {
                address += this.task.streetname;
              }
              if (this.task.house_number !== null) {
                address += ' ' + this.task.house_number;

                if (this.task.house_number_addition !== null) {
                  address += this.task.house_number_addition;
                }
              }
              if (this.task.city !== null) {
                address += (address !== '' ? ', ' : '') + this.task.city;
              }
              this.form.controls.address.setValue(address, {emitEvent: false});

              if (typeof response.data.handling !== 'undefined') {
                if (typeof response.data.handling.attachments !== 'undefined') {
                  if (response.data.handling.attachments.total && response.data.handling.attachments.media.length) {
                    response.data.handling.attachments.media.forEach((attachment: TaskResolutionNoteAttachment): void => {
                      if (attachment.type === 'video') {
                        this.handlingGalleryMedia.push(new VideoItem({
                          src: <any>[
                            {
                              url: attachment.src + '?ngsw-bypass=true',
                              type: attachment.mime,
                            }
                          ],
                          thumb: attachment.src,
                        }));
                      } else {
                        this.handlingGalleryMedia.push(new ImageItem({
                          src: attachment.src,
                          thumb: attachment.src,
                        }));
                      }
                    });
                    this.galleryHandlingRef.load(this.handlingGalleryMedia);
                  }
                }
              }

              if (typeof this.tabGroup !== 'undefined') {
                const hash: string = this.activatedRoute.snapshot.fragment;
                this.tabGroup._tabs.forEach((tab: MatTab): void => {
                  if (tab.textLabel.toLowerCase() === hash) {
                    if (tab.position === 3) {
                      this.tabGroup.selectedIndex = 0;
                    } else {
                      this.tabGroup.selectedIndex = tab.position;
                    }
                  }
                });
              }

              this.enableOrDisableTaskEditing();
              this.checkFormForRequiredFields();
              this.cd.detectChanges();
            }

            this.taskWebhookService.taskUpdated.pipe(takeUntil(this.destroy$)).subscribe((task: Task): void => {
              if (task && this.task && task.id === this.task.id) {
                this.enableOrDisableTaskEditing('disable');
                this.clearButtons();
                this.cd.detectChanges();
              }
            });

            this.taskWebhookService.taskDeleted.pipe(takeUntil(this.destroy$)).subscribe((task: Task): void => {
              if (task && this.task && task.id === this.task.id) {
                this.enableOrDisableTaskEditing('disable');
                this.clearButtons();
                this.cd.detectChanges();
              }
            });

            this.isLoading = false;
            this.updateFormValidity();
            this.cd.detectChanges();
          });
        }
      }
    });
  }

  getMatSelectData(dataName: string): any {
    if (typeof this.filters[dataName] !== 'undefined') {
      if (typeof this.filters[dataName].data !== 'undefined') {
        return this.filters[dataName].data;
      }
    }
  }

  refresh(taskId: number): void {
    this.taskWebhookService.clear();
    this.enableOrDisableTaskEditing('enable');
    this.taskPanelService.open(taskId);
  }

  tabChanged(event: MatTabChangeEvent): void {
    const tabName: string = event.tab.textLabel.toLowerCase();
    this.location.replaceState(this.location.path(false) + '#' + tabName);

    if (tabName === 'geschiedenis') {
      this.historyViewActivated.next();
    }
  }

  checkFormForRequiredFields(): void {
    Object.keys(this.form.controls).map((key: string): void => {
      const control = this.form.controls[key];
      if (control instanceof FormControl) {
        if (control.hasValidator(Validators.required)) {
          control.markAsTouched();
        }
      } else if (control instanceof FormGroup) {
        Object.keys(this.form.controls[key].controls).map((key2: string): void => {
          const control2 = this.form.controls[key].controls[key2];
          if (key === 'reporter' && key2 === 'email') {
          }
          if (control2 instanceof FormControl) {
            if (control2.hasValidator(Validators.required)) {
              control2.markAsTouched();
            }
          }
        });
      }
    });
  }

  getSearchedAddress(value: string): Observable<NationaalGeoregisterSuggest> {
    return new Observable<NationaalGeoregisterSuggest>((o: Subscriber<NationaalGeoregisterSuggest>): void => {
      this.clearAddressSuggestions();
      this.authenticationService.organisation$.pipe(takeUntil(this.destroy$)).subscribe((organisation: Organisation): void => {
        const url: string = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest' +
          '?q=' + encodeURIComponent(value) +
          '&rows=10' +
          '&bq=' + encodeURIComponent('type:provincie^0.5') +
          '&bq=' + encodeURIComponent('type:gemeente^0.5') +
          '&bq=' + encodeURIComponent('type:woonplaats^0.5') +
          '&bq=' + encodeURIComponent('type:weg^0.5') +
          '&bq=' + encodeURIComponent('type:adres^1.5') +
          '&fq=bron:"BAG"' +
          (organisation ?
            '&fq=gemeentenaam:(' + organisation.center_municipality + ')'
            : '');

        this.http.get(url, {
          observe: 'response',
          responseType: 'json'
        }).pipe(
          takeUntil(this.destroy$),
          timeout(5000)
        ).subscribe(
          (response: HttpResponse<NationaalGeoregisterSuggest>): void => {
            o.next(response.body);
          },
          (error): void => {
            if (error instanceof TimeoutError) {
              this.searchTaskLocationsTimedOut = true;
            }
            o.next(undefined);
          });
      });
    });
  }

  doReverseSearch(coords: [number, number]): void {
    const coords2 = this.leafletMapService.convertNewRdToLatLng(coords),
      url: string = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/reverse' +
        '?&lat=' + encodeURIComponent(coords2[1]) +
        '&lon=' + encodeURIComponent(coords2[0]) +
        '&rows=1&type=adres';

    this.isGeocodingNewRDCoordinate = true;

    this.http.get(url, {
      observe: 'response',
      responseType: 'json'
    }).subscribe(
      (response: HttpResponse<NationaalGeoregisterRevGeo>): void => {
        if (response.body.response.numFound > 0) {
          this.selectTaskAddress(response.body.response.docs[0].id);

          this.form.controls.lat.setValue(coords[0]);
          this.form.controls.lng.setValue(coords[1]);
        }
      },
      (): boolean => this.isGeocodingNewRDCoordinate = false
    );
  }

  selectTaskAddress(id: string): void {
    this.searchTaskLocations = [];
    this.searchTaskLocationsIsLoading = true;
    this.cd.detectChanges();

    const url: string = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/lookup' +
      '?id=' + encodeURIComponent(id);

    this.http.get(url, {
      observe: 'response',
      responseType: 'json'
    }).pipe(
      takeUntil(this.destroy$),
      timeout(5000)
    ).subscribe(
      (response: HttpResponse<NationaalGeoregisterLookup>): void => {
        if (response.body.response.numFound > 0) {
          const data = response.body.response.docs[0],
            dataLatLng: string[] = data.centroide_ll.replace('POINT(', '').replace(')', '').split(' '),
            dataNewRd: string[] = data.centroide_rd.replace('POINT(', '').replace(')', '').split(' ');
          if (!this.isGeocodingNewRDCoordinate) {
            this.form.controls.lat.setValue(parseFloat(dataLatLng[1]), {emitEvent: false});
            this.form.controls.lng.setValue(parseFloat(dataLatLng[0]), {emitEvent: false});
            this.form.controls.new_rd_lat.setValue(parseFloat(dataNewRd[0]));
            this.form.controls.new_rd_lng.setValue(parseFloat(dataNewRd[1]));
          }

          this.form.controls.address.setValue(
            data.straatnaam + (data.huis_nlt ? ' ' + data.huis_nlt : '') + ', ' + data.woonplaatsnaam
            , {emitEvent: false});
          this.form.controls.streetname.setValue(data.straatnaam ?? null, {emitEvent: false});
          this.form.controls.house_number.setValue((typeof data.huisnummer !== 'undefined' ? data.huisnummer : null), {emitEvent: false});
          this.form.controls.house_number_addition.setValue((typeof data.huisletter !== 'undefined' ? data.huisletter : null), {emitEvent: false});
          this.form.controls.postalcode.setValue(data.postcode ?? null, {emitEvent: false});
          this.form.controls.city.setValue(data.woonplaatsnaam ?? null, {emitEvent: false});

          if (typeof data.postcode === 'undefined') {
            const url2: string = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest' +
              '?q=' + encodeURIComponent(data.straatnaam + ' ' + (data.huisnummer ?? 1) + ' ' + data.woonplaatsnaam) +
              '&rows=1' +
              '&bq=' + encodeURIComponent('type:provincie^0.5') +
              '&bq=' + encodeURIComponent('type:gemeente^0.5') +
              '&bq=' + encodeURIComponent('type:woonplaats^0.5') +
              '&bq=' + encodeURIComponent('type:weg^0.5') +
              '&bq=' + encodeURIComponent('type:adres^1.5') +
              '&fq=(bron:"BAG") AND type:(adres AND postcode:*)'
            ;

            this.http.get(url2, {
              observe: 'response',
              responseType: 'json'
            }).pipe(
              takeUntil(this.destroy$),
              timeout(5000)
            ).subscribe(
              (response2: HttpResponse<NationaalGeoregisterSuggest>): void => {
                if (response2.body.response.numFound > 0) {

                  const url3: string = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/lookup' +
                    '?id=' + encodeURIComponent(response2.body.response.docs[0].id);

                  this.http.get(url3, {
                    observe: 'response',
                    responseType: 'json'
                  }).pipe(
                    takeUntil(this.destroy$),
                    timeout(5000)
                  ).subscribe(
                    (response3: HttpResponse<NationaalGeoregisterLookup>): void => {
                      if (response3.body.response.numFound > 0) {
                        const data2 = response3.body.response.docs[0];

                        this.form.controls.postalcode.setValue(data2.postcode ?? null, {emitEvent: false});
                      }
                    },
                    (error): void => {
                      if (error instanceof TimeoutError) {
                        this.searchTaskLocationsTimedOut = true;
                      }
                    });
                }

                this.updateFormValidity();
              },
              (error): void => {
                if (error instanceof TimeoutError) {
                  this.searchTaskLocationsTimedOut = true;
                }
              });
          } else {
            this.updateFormValidity();
          }
        }

        this.isGeocodingNewRDCoordinate = false;
        this.searchTaskLocationsIsLoading = false;
        this.cd.detectChanges();
      }
    );
  }

  clearAddressSuggestions(): void {
    this.searchTaskLocationsTimedOut = false;
    this.searchTaskLocationsIsLoading = false;
  }

  resetView(resetTabIndex: boolean = true): void {
    if (this.handlingAttachmentsRef.length) {
      this.handlingAttachmentsRef.forEach((ref: ComponentRef<RemovableAttachmentComponent>) => ref.destroy());
    }
    this.handlingAttachmentsRef = [];

    if (resetTabIndex && typeof this.tabGroup !== 'undefined') {
      this.tabGroup.selectedIndex = 0;
    }
    this.handlingForm.controls.handle.removeValidators(Validators.required);
    this.handlingForm.controls.handle.updateValueAndValidity({emitEvent: false});
    this.form.reset({
      number: '',
      created_at: '',
      priority_id: this.filters.priorities.data.find((d: Priority): boolean => d.default === true)?.id,
      status_id: this.filters.statuses.data.find((d: Status): boolean => d.name === 'to-do')?.id,
      assigned_user_id: null,
      lat: null,
      lng: null,
      new_rd_lat: null,
      new_rd_lng: null,
      address: null,
      streetname: null,
      house_number: null,
      house_number_addition: null,
      postalcode: null,
      city: null,
      basic_registration_id: null,
      task_registration_id: null,
      source_id: null,
      action_ids: null,
      current_id: null,
      impact_registration_id: null,
      outside_number: null,
    }, {emitEvent: false});

    this.handlingForm.reset({
      note: '',
      handle: null,
      standard_reaction_id: null,
      attachments: [],
      attachmentsContainer: '',
    }, {emitEvent: false});

    this.galleryHandlingRef.reset();
    this.handlingGalleryMedia = [];

    this.leafletMapService.clear();

    this.clearButtons();

    this.cd.detectChanges();
  }

  reassign(taskId: number): void {
    const dialogRef: MatDialogRef<ReassignDialogComponent> = this.dialog.open(ReassignDialogComponent, {
      disableClose: true
    });

    dialogRef.componentInstance.submitData.subscribe((formData: any): void => {
      if (dialogRef.componentInstance) {
        dialogRef.componentInstance.isLoading = true;
      }
      this.cd.detectChanges();

      this.taskService.reassign(taskId, formData).pipe(takeUntil(this.destroy$)).subscribe(
        (response: ServerResponse): void => {
          if (!this.api.isErrorResponse(response)) {
            if (dialogRef.componentInstance) {
              dialogRef.componentInstance.isLoading = false;
            }
            dialogRef.close();
            this.taskPanelService.close();
            this.taskService.sourceUpdated.next(true);
            this.cd.detectChanges();
          } else {
            dialogRef.componentInstance.isLoading = false;
          }
        },
        (): void => {
          if (dialogRef.componentInstance) {
            dialogRef.componentInstance.isLoading = false;
          }
          this.cd.detectChanges();
        }
      );
    });
  }

  sendReminder(taskId: number): void {
    const dialogRef: MatDialogRef<SendReminderDialogComponent> = this.dialog.open(SendReminderDialogComponent, {
      disableClose: true
    });

    dialogRef.componentInstance.submitData.subscribe((formData: any): void => {
      if (dialogRef.componentInstance) {
        dialogRef.componentInstance.isLoading = true;
      }
      this.cd.detectChanges();

      this.taskService.sendReminder(taskId, formData).pipe(takeUntil(this.destroy$)).subscribe(
        (response: ServerResponse): void => {
          if (!this.api.isErrorResponse(response)) {
            if (dialogRef.componentInstance) {
              dialogRef.componentInstance.isLoading = false;
            }
            dialogRef.close();
            this.taskPanelService.close();
            this.cd.detectChanges();
          } else {
            dialogRef.componentInstance.isLoading = false;
          }
        },
        (): void => {
          if (dialogRef.componentInstance) {
            dialogRef.componentInstance.isLoading = false;
          }
          this.cd.detectChanges();
        }
      );
    });
  }

  assignToMyself(taskId: number): void {
    this.isLoading = true;
    this.taskService.assignToMyself(this.taskId).pipe(takeUntil(this.destroy$)).subscribe(
      (response: ServerResponse): void => {
        if (!this.api.isErrorResponse(response)) {
          this.isLoading = false;

          this.taskPanelService.open(taskId);
          this.taskService.sourceUpdated.next(true);
          this.cd.detectChanges();
        } else {
          this.isLoading = false;
        }
      },
      (): void => {
        this.isLoading = false;
        this.cd.detectChanges();
      }
    );
  }

  toggleTaskEditing(): void {
    this.editCurrentTask = !this.editCurrentTask;

    if (this.editCurrentTask) {
      this.formService.populateForm(this.task, [
        'basic_registration_id',
      ]);

      if (typeof this.task.action_ids !== 'undefined') {
        this.form.controls.action_ids.setValue(this.task.action_ids);
      }
    }
    this.clearButtons();
    this.updateButtons();
  }

  save(): void {
    const saveButton: IndexButton = this.bottomRightButtons.find((d: IndexButton): boolean => d.id === 'save-task'),
      formValue = {
        ...this.form.value,
        ...{
          created_at: this.form.value.created_at ? moment(this.form.value.created_at).toISOString() : null,
        }
      };
    if (typeof saveButton !== 'undefined') {
      saveButton.enabled = false;
    }
    this.isLoading = true;
    this.cd.detectChanges();
    if (this.taskPanelService.editorType === 'create') {
      this.taskService.create(formValue).pipe(takeUntil(this.destroy$)).subscribe(
        (data: ServerResponse): void => {
          this.isLoading = false;

          if (!this.api.isErrorResponse(data)) {
            this.taskPanelService.close();
            this.taskService.sourceUpdated.next(true);
          }
          this.cd.detectChanges();
        },
        (): void => {
          this.isLoading = false;
          if (typeof saveButton !== 'undefined') {
            saveButton.enabled = true;
          }
          this.cd.detectChanges();
        }
      );
    } else {
      this.taskService.update(this.taskId, formValue).pipe(takeUntil(this.destroy$)).subscribe(
        (data: ServerResponse): void => {
          this.isLoading = false;
          this.cd.detectChanges();

          if (!this.api.isErrorResponse(data)) {
            this.taskPanelService.close();
            this.taskService.sourceUpdated.next(true);
          }
          if (typeof saveButton !== 'undefined') {
            saveButton.enabled = true;
          }
          this.cd.detectChanges();
        },
        (): void => {
          this.isLoading = false;
          if (typeof saveButton !== 'undefined') {
            saveButton.enabled = true;
          }
          this.cd.detectChanges();
        }
      );
    }
  }

  selectTaskLocation(): void {
    const dialogRef: MatDialogRef<LocationSelectorDialogComponent> = this.dialog.open(LocationSelectorDialogComponent, {
      data: this.form.controls,
      disableClose: true,
      panelClass: 'wide'
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result) {
        this.form.controls.lat.setValue(result.lat);
        this.form.controls.lng.setValue(result.lng);
        this.form.controls.new_rd_lat.setValue(result.new_rd_lat);
        this.form.controls.new_rd_lng.setValue(result.new_rd_lng);
        this.form.controls.address.setValue(result.address.weergavenaam, {emitEvent: false});
        this.form.controls.streetname.setValue(result.streetname);
        this.form.controls.house_number.setValue(result.house_number);
        this.form.controls.house_number_addition.setValue(result.house_number_addition);
        this.form.controls.postalcode.setValue(result.postalcode);
        this.form.controls.city.setValue(result.city);
      }
    });
  }

  handleTask(): void {
    this.isLoading = true;

    const handleButton: IndexButton = this.bottomRightButtons.find((d: IndexButton): boolean => d.id === 'handle-button');
    if (typeof handleButton !== 'undefined') {
      handleButton.enabled = false;
    }
    this.taskService.handle(this.taskId, this.handlingForm.value).pipe(takeUntil(this.destroy$)).subscribe(
      (data: ServerResponse): void => {
        this.isLoading = false;
        this.cd.detectChanges();

        if (!this.api.isErrorResponse(data)) {
          this.taskPanelService.close();
          this.resetView(false);
          this.taskService.sourceUpdated.next(true);
        }
        if (typeof handleButton !== 'undefined') {
          handleButton.enabled = true;
        }
        this.cd.detectChanges();
      },
      (): void => {
        this.isLoading = false;
        if (typeof handleButton !== 'undefined') {
          handleButton.enabled = true;
        }
        this.cd.detectChanges();
      }
    );
  }

  getTooltipText(e: MatFormField): string {
    const label: HTMLLabelElement = e._elementRef.nativeElement.querySelector('.mat-form-field-label') as HTMLLabelElement;
    const matLabel: HTMLLabelElement = e._elementRef.nativeElement.querySelector('.mat-form-field-label mat-label') as HTMLLabelElement;

    if (matLabel) {
      const children: HTMLCollection = matLabel.children;
      for (let i: number = 0; i < children.length; i++) {
        if (children[i].tagName === 'SPAN') {
          return label.scrollWidth > label.clientWidth ? children[i].innerHTML : null;
        }
      }
    }

    return null;
  }

  detectHandlingAttachments(event: any): void {
    if (typeof event !== 'undefined') {
      if (typeof event.target !== 'undefined') {
        if (typeof event.target.files !== 'undefined') {
          const formArray: FormArray = this.handlingForm.get('attachments') as FormArray;

          Object.keys(event.target.files).map((file: string): void => {
            const component: ComponentRef<RemovableAttachmentComponent> = this.handlingAttachmentsPlaceholder.createComponent(RemovableAttachmentComponent);
            component.instance.attachment = event.target.files[file];
            component.instance.filename = event.target.files[file].name;
            component.instance.size = event.target.files[file].size;

            this.handlingAttachmentsRef.push(component);

            const control: FormControl<File> = new FormControl(event.target.files[file]);
            formArray.push(control);

            component.instance.destroy.subscribe((): void => {
              const index: number = this.handlingAttachmentsRef.indexOf(component);
              formArray.removeAt(index);
              component.destroy();
            });
          });
        }
      }
    }
  }

  forwardTaskDialog(id: number): void {
    const dialog: MatDialogRef<ForwardDialogComponent> = this.dialog.open(ForwardDialogComponent, {
      data: id,
      disableClose: true,
    });

    dialog.componentInstance.submitData.pipe(take(1)).subscribe((): void => {
      dialog.close();
      this.cd.detectChanges();
    });
  }

  delete(id: number): void {
    this.isLoading = true;
    this.cd.detectChanges();

    this.taskService.delete(id).pipe(takeUntil(this.destroy$)).subscribe((response: boolean): void | boolean => {
      if (response) {
        this.taskPanelService.close();
        this.taskService.sourceUpdated.next();
      }
      this.isLoading = false;
      this.cd.detectChanges();
    });
  }

  restore(id: number): void {
    this.isLoading = true;
    this.cd.detectChanges();

    this.taskService.restore(id).pipe(takeUntil(this.destroy$)).subscribe((response: boolean): void | boolean => {
      if (response) {
        this.taskPanelService.open(id);
        this.taskService.sourceUpdated.next();
      }
      this.isLoading = false;
      this.cd.detectChanges();
    });
  }

  toggleReopen(): void {
    this.showReopenWidget = !this.showReopenWidget;

    const index: number = this.tabGroup._tabs.toArray().findIndex((tab: MatTab): boolean => tab.textLabel.toLowerCase() === 'afhandeling'),
      reopenButton: number = this.bottomRightButtons.findIndex((d: IndexButton): boolean => d.id === 'reopen');

    if (reopenButton !== -1) {
      this.bottomRightButtons[reopenButton].enabled = !this.showReopenWidget;
    }

    if (index !== -1) {
      this.tabGroup.selectedIndex = index;
    }

    if (!this.showReopenWidget) {
      this.reopenForm.controls.note.setValue('');
    }
  }

  reopenTask(): void {
    this.isLoading = true;

    this.taskService.reopen(this.taskId, this.reopenForm.value).pipe(takeUntil(this.destroy$)).subscribe(
      (data: ServerResponse): void => {
        this.isLoading = false;
        this.cd.detectChanges();

        if (!this.api.isErrorResponse(data)) {
          this.taskPanelService.close();
          this.resetView(false);
          this.taskService.sourceUpdated.next(true);
        }
        this.cd.detectChanges();
      },
      (): void => {
        this.isLoading = false;
        this.cd.detectChanges();
      }
    );
  }

  taskBug(): void {
    if (typeof window.Marker !== 'undefined') {
      window.Marker.capture();
    }
  }

  updateButtons(): void {
    this.authenticationService.user$.pipe(takeUntil(this.destroy$)).subscribe((user: User): void => {
      if (user) {
        let topButtonStartPosition: number = 0;

        // "Reopen" / "Heropen" button, which reopens a closed task
        if (
          this.task &&
          (typeof this.task.is_deleted === 'undefined' || !this.task.is_deleted) &&
          ['resolved', 'rejected'].indexOf(this.task.status.name) !== -1 &&
          user.permissions.includes('tasks.reopen')
        ) {
          const reopenButtonIndex: number = this.bottomRightButtons.findIndex((d: IndexButton): boolean => d.id === 'reopen');
          if (reopenButtonIndex === -1) {
            this.bottomRightButtons.push({
              id: 'reopen',
              text: 'Heropen',
              functionOrRoute: this.toggleReopen.bind(this),
              color: 'primary',
              icon: 'refresh'
            });
          }
        }

        this.canShowHandleButtons = this.task && (typeof this.task.is_deleted === 'undefined' || !this.task.is_deleted) &&
          ['resolved', 'rejected'].indexOf(this.task.status.name) === -1 && user.permissions.includes('tasks.handle');

        // "Forward" / "Kopie van taak versturen" button, which forwards a task
        if (
          this.task &&
          (typeof this.task.is_deleted === 'undefined' || !this.task.is_deleted) &&
          user.permissions.includes('tasks.forward')
        ) {
          const forwardTaskButtonIndex: number = this.topButtons.findIndex((d: IndexButton): boolean => d.id === 'forward-task');
          if (forwardTaskButtonIndex === -1) {
            this.topButtons.splice(topButtonStartPosition++, 0, {
              id: 'forward-task',
              tooltip: 'E-Mail',
              functionOrRoute: this.forwardTaskDialog.bind(this, this.taskId),
              color: 'black',
              icon: 'email'
            });
          }
        }

        // "Print" / "Printen" task
        if (
          this.task &&
          user.permissions.includes('tasks.print')
        ) {
          const printButtonIndex: number = this.topButtons.findIndex((d: IndexButton): boolean => d.id === 'print-task');
          if (printButtonIndex === -1) {
            this.topButtons.splice(topButtonStartPosition++, 0, {
              id: 'print-task',
              tooltip: 'Printen',
              functionOrRoute: this.taskPrintService.print.bind(this, this.task),
              color: 'black',
              icon: 'print'
            });
          }
        }

        // "Delete" / "Verwijderen" / "Restore" buttons, which deletes or restores deleted tasks
        if (this.task && user.permissions.includes('tasks.delete')) {
          if (this.task.is_deleted && user.role === 'super-admin') {
            const deleteTaskButtonIndex: number = this.topButtons.findIndex((d: IndexButton): boolean => d.id === 'delete-task'),
              restoreTaskButtonIndex: number = this.topButtons.findIndex((d: IndexButton): boolean => d.id === 'restore-task');
            if (restoreTaskButtonIndex === -1) {
              this.topButtons.splice(topButtonStartPosition++, 0, {
                id: 'restore-task',
                tooltip: 'Restore',
                functionOrRoute: this.restore.bind(this, this.task.id),
                color: 'primary',
                icon: 'refresh'
              });
            }
            if (deleteTaskButtonIndex === -1) {
              this.topButtons.splice(topButtonStartPosition++, 0, {
                id: 'delete-task',
                tooltip: 'Verwijderen',
                functionOrRoute: this.delete.bind(this, this.task.id),
                color: 'accent',
                icon: 'delete'
              });
            }
          }
          if (!this.task.is_deleted) {
            const deleteTaskButtonIndex: number = this.topButtons.findIndex((d: IndexButton): boolean => d.id === 'delete-task');
            if (deleteTaskButtonIndex === -1) {
              this.topButtons.splice(topButtonStartPosition++, 0, {
                id: 'delete-task',
                tooltip: 'Verwijderen',
                functionOrRoute: this.delete.bind(this, this.task.id),
                color: 'accent',
                icon: 'delete'
              });
            }
          }
        }

        // "Reminder" / "Herinnering" button
        if (
          this.task &&
          (typeof this.task.is_deleted === 'undefined' || !this.task.is_deleted) &&
          ['resolved', 'rejected'].indexOf(this.task.status.name) === -1 &&
          user.permissions.includes('tasks.send-reminder') &&
          !this.editCurrentTask
        ) {
          const sendReminderButtonIndex: number = this.topButtons.findIndex((d: IndexButton): boolean => d.id === 'reminder');
          if (sendReminderButtonIndex === -1) {
            this.topButtons.splice(0, 0, {
              id: 'reminder',
              tooltip: 'Herinnering',
              functionOrRoute: this.sendReminder.bind(this, this.taskId),
              color: 'warn',
              icon: 'notifications'
            });
          }
        }

        // "Assign" / "Toewijzen" button, which assigns a task to the selected user
        if (
          this.task &&
          (typeof this.task.is_deleted === 'undefined' || !this.task.is_deleted) &&
          ['resolved', 'rejected'].indexOf(this.task.status.name) === -1 &&
          user.permissions.includes('tasks.reassign') &&
          !this.editCurrentTask
        ) {
          const assignToUserButtonIndex: number = this.bottomRightButtons.findIndex((d: IndexButton): boolean => d.id === 'assign-to-user');
          if (assignToUserButtonIndex === -1) {
            this.bottomRightButtons.push({
              id: 'assign-to-user',
              text: 'Toewijzen',
              functionOrRoute: this.reassign.bind(this, this.taskId),
              color: 'primary',
              icon: 'account_circle'
            });
          }
        }

        // "Processing" / "In behandeling nemen" button, which marks tasks as in handling and assigns to current user
        if (
          this.task &&
          (typeof this.task.is_deleted === 'undefined' || !this.task.is_deleted) &&
          ['resolved', 'rejected', 'in-progress'].indexOf(this.task.status.name) === -1 &&
          //          (!this.task.assigned_user_id || this.task.assigned_user_id !== user.id) &&
          user.permissions.includes('tasks.handle') &&
          !this.editCurrentTask
        ) {
          const assignToMyselfButtonIndex: number = this.bottomRightButtons.findIndex((d: IndexButton): boolean => d.id === 'assign-to-myself');
          if (assignToMyselfButtonIndex === -1) {
            this.bottomRightButtons.push({
              id: 'assign-to-myself',
              text: 'In behandeling nemen',
              functionOrRoute: this.assignToMyself.bind(this, this.taskId),
              color: 'secondary',
              icon: 'assignment'
            });
          }
        }

        // cancel editing
        if (
          (
            this.task &&
            (typeof this.task.is_deleted === 'undefined' || !this.task.is_deleted) &&
            (['resolved', 'rejected'].indexOf(this.task.status.name) === -1 && user.permissions.includes('tasks.update'))
          ) &&
          this.editCurrentTask
        ) {
          const cancelTaskButtonIndex: number = this.bottomRightButtons.findIndex((d: IndexButton): boolean => d.id === 'cancel-task');
          if (cancelTaskButtonIndex === -1) {
            this.bottomRightButtons.push({
              id: 'cancel-task',
              text: 'Annuleren',
              functionOrRoute: this.toggleTaskEditing.bind(this),
              color: 'accent',
              icon: 'close',
            });
          }
        }

        // "Save" / "Opslaan" button, which Creates new or updates existing task
        if (
          (
            !this.task &&
            user.permissions.includes('tasks.create')
          ) ||
          (
            this.task &&
            (typeof this.task.is_deleted === 'undefined' || !this.task.is_deleted) &&
            (['resolved', 'rejected'].indexOf(this.task.status.name) === -1 && user.permissions.includes('tasks.update'))
          ) &&
          this.editCurrentTask
        ) {
          const saveTaskButtonIndex: number = this.bottomRightButtons.findIndex((d: IndexButton): boolean => d.id === 'save-task');
          if (saveTaskButtonIndex === -1) {
            this.bottomRightButtons.push({
              id: 'save-task',
              text: 'Opslaan',
              functionOrRoute: this.save.bind(this),
              color: 'primary',
              icon: 'download',
              enabled: false
            });
            this.updateFormValidity();
          }
        }

        // "Log" button that prints the form's data into the console
        if (
          user.email === 'k.ortego@soliddigital.nl' ||
          user.impersonated_by && user.impersonated_by.email === 'k.ortego@soliddigital.nl'
        ) {
          const logButtonIndex: number = this.bottomRightButtons.findIndex((d: IndexButton): boolean => d.id === 'log-button');
          if (logButtonIndex === -1) {
            this.bottomRightButtons.push({
              id: 'log-button',
              text: 'Log',
              functionOrRoute: this.log.bind(this),
              color: 'primary',
              icon: 'list'
            });
          } else {
            const [button] = this.bottomRightButtons.splice(logButtonIndex, 1);
            this.bottomRightButtons.push(button);
          }
        }

        if (this.task) {
          this.taskService.listOfTaskIdsUpdated.pipe(takeUntil(this.destroy$)).subscribe((): void => {
            // handle Previous button
            const previousButtonIndex: number = this.topButtons.findIndex((d: IndexButton): boolean => d.id === 'previous-task'),
              previousId: number = this.taskService.getPreviousId(this.task.id);

            if (!previousId && previousButtonIndex !== -1) {
              this.topButtons.splice(previousButtonIndex, 1);
            }

            if (previousButtonIndex === -1 && previousId) {
              this.topButtons.push({
                id: 'previous-task',
                tooltip: 'Vorige',
                icon: 'chevron_left',
                color: 'white',
                functionOrRoute: this.taskPanelService.open.bind(this.taskPanelService, previousId),
              });
            } else if (previousButtonIndex !== -1 && previousId) {
              this.topButtons[previousButtonIndex].functionOrRoute = this.taskPanelService.open.bind(this.taskPanelService, previousId);
            }

            // handle Next button
            const nextButtonIndex: number = this.topButtons.findIndex((d: IndexButton): boolean => d.id === 'next-task'),
              nextId: number = this.taskService.getNextId(this.task.id);

            if (!nextId && nextButtonIndex !== -1) {
              this.topButtons.splice(nextButtonIndex, 1);
            }

            if (nextButtonIndex === -1 && nextId) {
              this.topButtons.push({
                id: 'next-task',
                tooltip: 'Volgende',
                icon: 'chevron_right',
                iconPosition: 'right',
                color: 'white',
                functionOrRoute: this.taskPanelService.open.bind(this.taskPanelService, nextId),
              });
            } else if (nextButtonIndex !== -1 && nextId) {
              this.topButtons[nextButtonIndex].functionOrRoute = this.taskPanelService.open.bind(this.taskPanelService, nextId);
            }
          });
        }
      }
    });
  }

  clearButtons(): void {
    const keepButtonIds: string[] = [
      'close-task',
      'submit-feedback',
      'log-button',
    ];

    this.topButtons = this.topButtons.filter((d: IndexButton): boolean => keepButtonIds.includes(d.id));
    this.bottomLeftButtons = this.bottomLeftButtons.filter((d: IndexButton): boolean => keepButtonIds.includes(d.id));
    this.bottomRightButtons = this.bottomRightButtons.filter((d: IndexButton): boolean => keepButtonIds.includes(d.id));
  }

  updateFormValidity(): void {
    const saveButton: IndexButton = this.bottomRightButtons.find((d: IndexButton): boolean => d.id === 'save-task'),
      handleButton: IndexButton = this.bottomRightButtons.find((d: IndexButton): boolean => d.id === 'handle-task');
    if (typeof saveButton !== 'undefined') {
      saveButton.enabled = this.form.valid;
    }
    if (
      typeof handleButton !== 'undefined' &&
      this.task.handling &&
      ['resolved', 'rejected'].indexOf(this.task.status.name) === -1 &&
      this.handlingForm.controls.handle.value
    ) {
      handleButton.enabled = this.handlingForm.valid && this.canShowHandleButtons;
    }
    this.toggleTabWarningIcon();

    this.cd.detectChanges();
  }

  enableOrDisableTaskEditing(event: 'enable' | 'disable' = 'enable'): void {
    this.authenticationService.user$.pipe(takeUntil(this.destroy$)).subscribe((user: User): void => {
      let disableForm: boolean = false;

      if (this.task) {
        if (event === 'disable') {
          disableForm = true;
        } else {
          if (!user.permissions.includes('tasks.closed.edit') && (this.task.status.name === 'rejected' || this.task.status.name === 'resolved')) {
            disableForm = true;
          }

          if (!user.permissions.includes('tasks.update')) {
            disableForm = true;
          }

          if (this.task.status.name === 'in-review') {
            disableForm = true;
          }
        }
      }

      if (user.role !== 'super-admin') {
        if (disableForm) {
          this.disableForm = true;
          this.form.disable({emitEvent: false});
          this.form.updateValueAndValidity({emitEvent: false});

          if (!this.canShowHandleButtons) {
            this.handlingForm.disable({emitEvent: false});
            this.handlingForm.updateValueAndValidity({emitEvent: false});
          }
        } else {
          this.disableForm = false;
          this.form.enable({emitEvent: false});
          this.form.updateValueAndValidity({emitEvent: false});
        }

        this.cd.detectChanges();
      }
    });
  }

  toggleTabWarningIcon(): void {
    const taskControls: string[] = [
        'number',
        'created_at',
        'priority_id',
        'status_id',
        'assigned_user_id',
        'new_rd_lat',
        'new_rd_lng',
        'address',
        'basic_registration_id',
        'task_registration_id',
        'source_id',
        'action_ids',
        'current_id',
        'impact_registration_id',
        'outside_number',
      ],
      handlingControls: string[] = [
        'handle',
        'note',
        'standard_reaction_id',
      ],
      taskIndex: number = this.tabWarning.findIndex((d: string): boolean => d === 'task'),
      handlingIndex: number = this.tabWarning.findIndex((d: string): boolean => d === 'handling');
    let showTaskError: boolean = false,
      showHandlingError: boolean = false;
    taskControls.forEach((control: string): void => {
      if (this.form.get(control).errors !== null) {
        showTaskError = true;
      }
    });
    if (showTaskError) {
      if (taskIndex === -1) {
        this.tabWarning.push('task');
      }
    } else {
      if (taskIndex !== -1) {
        this.tabWarning.splice(taskIndex, 1);
      }
    }
    handlingControls.forEach((control: string): void => {
      if (this.handlingForm.get(control).errors !== null) {
        showHandlingError = true;
      }
    });
    if (showHandlingError) {
      if (handlingIndex === -1) {
        this.tabWarning.push('handling');
      }
    } else {
      if (handlingIndex !== -1) {
        this.tabWarning.splice(handlingIndex, 1);
      }
    }
  }

  editNote(type: 'create' | 'edit' | 'cancel' | 'save', note: TaskNote = null): void {
    if (type === 'edit') {
      this.noteForm.controls.update_id.setValue(note.id);
      this.noteForm.controls.note.setValue(note.note);
    } else if (type !== 'save') {
      this.noteForm.controls.update_id.setValue(null);
      this.noteForm.controls.note.setValue('');
    }
    this.showNoteWidget = type !== 'cancel';

    if (type === 'save') {
      this.isLoading = true;
      this.cd.detectChanges();

      if (this.noteForm.controls.update_id.value) {
        this.taskService.updateNote(this.taskId, this.noteForm.controls.update_id.value, this.noteForm.value).pipe(takeUntil(this.destroy$)).subscribe(
          (response: ServerResponse): void => {
            if (!this.api.isErrorResponse(response)) {
              this.taskPanelService.open(this.taskId);
              this.taskService.sourceUpdated.next();

              this.noteForm.controls.update_id.setValue(null);
              this.noteForm.controls.note.setValue('');
              this.showNoteWidget = false;
            }
            this.isLoading = false;
            this.cd.detectChanges();
          });
      } else {
        this.taskService.createNote(this.taskId, this.noteForm.value).pipe(takeUntil(this.destroy$)).subscribe(
          (response: ServerResponse): void => {
            if (!this.api.isErrorResponse(response)) {
              this.taskPanelService.open(this.taskId);
              this.taskService.sourceUpdated.next();

              this.noteForm.controls.update_id.setValue(null);
              this.noteForm.controls.note.setValue('');
              this.showNoteWidget = false;
            }
            this.isLoading = false;
            this.cd.detectChanges();
          });
      }
    } else if (type === 'cancel') {
      this.showNoteWidget = false;
      this.cd.detectChanges();
    }
  }

  setHandleValue(value: 'resolved' | 'rejected'): void {
    this.handlingForm.patchValue({
      handle: value,
      note: '',
      standard_reaction_id: null,
    });

    this.cd.detectChanges();
  }

  clearValue(field: string): void {
    this.form.get(field).setValue(null);
  }

  getTileAssignedUsers(users: User[]): string {
    return users.map((user: User): string => user.name + (user.departments.length ? '(' + user.departments.map(d => d.name).join(', ') + ')' : '')).join(', ');
  }

  getDepartments(departments: Department[]): string {
    return departments.map((department: Department): string => department.name).join(', ');
  }

  geocodeNewRDCoordinate(): void {
    const lat: number = parseFloat(this.form.controls.new_rd_lat.value + ''),
      lng: number = parseFloat(this.form.controls.new_rd_lng.value + '');

    if (lat && lng) {
      this.doReverseSearch([lat, lng]);
    }
  }

  log(): void {
    console.log(this.form);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.api.filterPageName = this.previousPageFilter;

    this.dateTimeService.cd = null;

    this.leafletMapService.clear();
    this.taskPanelService.close();

    this.isLoading = false;
  }

}
